/**
 * Calculate a discounted price from an original amount and discount rate.
 *
 * IMPORTANT: This applies the discount to the TOTAL amount, not per-unit,
 * to avoid compounded rounding errors when multiplying by quantity.
 *
 * @param originalCents - The original price in cents (integer)
 * @param discountRate - The discount percentage (0-100)
 * @returns The discounted price in cents (integer, rounded)
 *
 * @example
 * // 200 credits at 10 cents each with 75% discount
 * const originalTotal = 200 * 10; // 2000 cents ($20)
 * const discounted = calculateDiscountedPriceCents(originalTotal, 75);
 * // Returns 500 cents ($5), not 600 cents
 */
export function calculateDiscountedPriceCents(
  originalCents: number,
  discountRate: number,
): number {
  if (discountRate <= 0) {
    return originalCents;
  }

  // Apply discount to total to avoid per-unit rounding errors
  return Math.round(originalCents * (1 - discountRate / 100));
}

/**
 * Calculate a discounted price in dollars from an original dollar amount.
 *
 * @param originalDollars - The original price in dollars
 * @param discountRate - The discount percentage (0-100)
 * @returns The discounted price in dollars (rounded to 2 decimal places)
 */
export function calculateDiscountedPriceDollars(
  originalDollars: number,
  discountRate: number,
): number {
  if (discountRate <= 0) {
    return originalDollars;
  }

  // Convert to cents, apply discount, round, then convert back to dollars
  const originalCents = Math.round(originalDollars * 100);
  const discountedCents = calculateDiscountedPriceCents(
    originalCents,
    discountRate,
  );
  return discountedCents / 100;
}
